import { Col, Row } from "react-bootstrap";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { productVersionsMappingsApi, versionsMasterDataApi } from "@/apis/apis";
import { useGet, usePut } from "@/apiHooks/HttpFunctions";

import AsyncDropdown from "@/components/AsyncDropdown/AsyncDropdown";
import BasicInput from "@/components/BasicInput";
import BasicModal from "@/Modals/BasicModal";
import { LIST_MODE } from "@/constant/Lists/listMode";
import ProductVersionInitialValues from "@/data/WebDataManagement/Mapping/ProductVersion/initial-values";
import ProductVersionSchema from "@/schema/WebDataManagement/Mapping/ProductVersion/ProductVersionSchema";
import RenderToast from "@/components/RenderToast";
import axiosInstance from "@/Axios/ConfiguredAxios";
import { debounce } from "@mui/material";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";

const ProductVersionModal = forwardRef((_, ref) => {
  const [defaultOptions, setDefaultOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [saveError, setSaveError] = useState(null);
  const resetError = useCallback(() => {
    setSaveError(null);
  }, []);

  const [modalState, setModalState] = useState({ isOpen: false, id: null });

  const showApi = modalState.id
    ? `${productVersionsMappingsApi}/${modalState.id}`
    : null;

  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });

  const listApi = `${versionsMasterDataApi}?mode=${LIST_MODE.SELECT}&filter[keyword]=`;

  useEffect(() => {
    if (!modalState.isOpen) return;

    const fetchDefaultOptions = async () => {
      setLoadingOptions(true);
      const response = await axiosInstance.get(listApi);

      if (response) {
        const options =
          response.data.data.map((val) => ({
            label: `${val.code} - ${val.name}`,
            value: val.id,
          })) ?? [];

        const selectedVersions = showData?.data?.versions || [];
        selectedVersions.forEach((version) => {
          if (!options.some((opt) => opt.value === version.id)) {
            options.unshift({
              label: `${version.code} - ${version.name}`,
              value: version.id,
            });
          }
        });

        setDefaultOptions(options);
      }

      setLoadingOptions(false);
    };

    fetchDefaultOptions();
  }, [listApi, modalState, showData]);

  const debouncedFetchOptions = debounce(async (input, callback) => {
    setLoadingOptions(true);
    const response = await axiosInstance.get(`${listApi}${input}`);

    if (response) {
      const newOptions =
        response.data.data
          .filter((item) => item.id !== modalState.id)
          .map((item) => ({
            label: `${item.code} - ${item.name}`,
            value: item.id,
          })) ?? [];

      // Append new options to defaultOptions if not already present
      setDefaultOptions((prev) => {
        const existingIds = prev.map((opt) => opt.value);
        return [
          ...prev,
          ...newOptions.filter((option) => !existingIds.includes(option.value)),
        ];
      });

      callback(newOptions);
    }

    setLoadingOptions(false);
  }, 500);

  const getSelectedOption = () => {
    if (!formik.values.version_ids?.length) return [];
    return formik.values.version_ids.map((id) => {
      // Attempt to find the option in the defaultOptions
      const foundOption = defaultOptions.find((opt) => opt.value === id);
      if (foundOption) {
        return foundOption;
      }

      // Fallback: Dynamically create the option
      const matchingVersion = showData?.data?.versions.find((v) => v.id === id);
      return {
        label: matchingVersion
          ? `${matchingVersion.code} - ${matchingVersion.name}`
          : `Unknown (${id})`,
        value: id,
      };
    });
  };

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Product - Version ${action} successfully.`,
    });
  };

  const {
    mutate: saveEdit,
    isPending: saveIsPending,
    error: saveEditError,
  } = usePut({
    apiURL: `${productVersionsMappingsApi}/${modalState.id}`,
    onSuccess: handleSuccess("edited"),
  });

  useEffect(() => {
    setSaveError(saveEditError);
  }, [saveEditError]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: ProductVersionInitialValues(modalState.id ? showData : null),
    validationSchema: ProductVersionSchema(),
    onSubmit: (values) => handleSubmit(values),
  });

  const handleSubmit = (values) => {
    const payload = {
      version_ids: values.version_ids,
    };
    saveEdit(JSON.stringify(payload));
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [resetError, formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  if (!showData) return null;

  return (
    <BasicModal
      header={
        modalState.id ? "Edit Product - Version" : "Add Product - Version"
      }
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            saveError={saveError}
            label="Product"
            disabled={true}
            value={`${showData.data.code} - ${showData.data.name}`}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <AsyncDropdown
            isMulti={true}
            label="Versions"
            placeholder="No version selected. Type to search..."
            defaultOptions={defaultOptions}
            loadOptions={debouncedFetchOptions}
            isLoading={loadingOptions}
            value={getSelectedOption()}
            onChange={(selected) => {
              if (selected) {
                // Add selected option to defaultOptions if not already present
                setDefaultOptions((prev) => {
                  if (!prev.some((opt) => opt.value === selected.value)) {
                    return [
                      ...prev,
                      { label: selected.label, value: selected.value },
                    ];
                  }
                  return prev;
                });

                // Update formik value
                formik.setFieldValue(
                  "version_ids",
                  selected.map((sel) => sel.value)
                );
              } else {
                formik.setFieldValue("version_ids", []);
              }
            }}
          />
        </Col>
      </Row>
    </BasicModal>
  );
});

export default ProductVersionModal;
